/**
 * Fonte única de verdade dos passos da jornada em /infos-festa.
 *
 * O stepper renderizado e o auto-advance (retomada de um orçamento/evento já
 * iniciado) precisam usar exatamente a mesma lista de passos ativos — caso
 * contrário o índice calculado em uma base de 4 passos acaba aplicado em uma
 * régua de 3 passos, fazendo a jornada abrir no passo errado.
 */

export type JourneyStepKey = 'event' | 'address' | 'time' | 'data';

export interface JourneyStepFlags {
  /** Passo 1 (Tipo de Evento) oculto */
  hideEvent: boolean;
  /** Passo "Data e Horário" oculto pela configuração da Jornada do Cliente */
  hideDatetime: boolean;
  /** Somente produtos de entrega: não há horário de festa a definir */
  hasOnlyDeliveryProducts: boolean;
}

export type JourneyStepCompletion = Partial<Record<JourneyStepKey, boolean>>;

/** Lista ordenada dos passos realmente exibidos ao cliente. */
export function buildJourneyStepKeys(flags: JourneyStepFlags): JourneyStepKey[] {
  const keys: JourneyStepKey[] = [];

  if (!flags.hideEvent) keys.push('event');

  // O endereço é sempre exigido (nome do local, número e complemento).
  keys.push('address');

  if (!flags.hasOnlyDeliveryProducts && !flags.hideDatetime) keys.push('time');

  keys.push('data');

  return keys;
}

/**
 * Passo inicial (1-based) para uma jornada retomada.
 *
 * Regras:
 * - vai para o primeiro passo pendente da lista ativa;
 * - nunca ultrapassa o passo "Endereço", mesmo que CEP/endereço já venham do
 *   carrinho, porque o cliente ainda precisa confirmar nome do local, número
 *   e complemento.
 */
export function resolveInitialJourneyStep(
  keys: JourneyStepKey[],
  completion: JourneyStepCompletion,
): number {
  if (keys.length === 0) return 1;

  const firstIncompleteIdx = keys.findIndex((key) => !completion[key]);
  const target = firstIncompleteIdx === -1 ? keys.length : firstIncompleteIdx + 1;

  const addressIdx = keys.indexOf('address');
  const maxStep = addressIdx === -1 ? keys.length : addressIdx + 1;

  return Math.max(1, Math.min(target, maxStep));
}
